import * as XLSX from "xlsx";
import type { Column } from "../components/ui/DataTable";

/**
 * Zet een cel-waarde om naar een Excel-vriendelijk type: numerieke strings
 * (eventueel met €, punt-duizendtallen en komma-decimalen) worden echte
 * getallen zodat ze in Excel sommeerbaar zijn.
 */
function toCell(value: unknown): string | number {
  if (typeof value === "number") return value;
  const s = String(value ?? "").trim();
  if (!s) return s;
  // € en spaties weg, dan normaliseren: "1.234,56" → "1234.56".
  const numeric = s
    .replace(/€/g, "")
    .replace(/\s/g, "")
    .replace(/\./g, "")
    .replace(",", ".");
  if (numeric && /^-?\d+(\.\d+)?$/.test(numeric)) return Number(numeric);
  return s;
}

/**
 * Exporteer rijen naar een echt .xlsx-bestand (SheetJS) en trigger een download.
 * Kolommen zonder header (bijv. de actions-kolom) worden overgeslagen; waarden
 * komen uit col.accessor (zelfde keuze als CSV/print).
 */
export function exportRowsToXlsx<T>(
  rows: T[],
  columns: Column<T>[],
  filename: string
): void {
  const cols = columns.filter((c) => c.header.trim() !== "");
  const aoa: (string | number)[][] = [
    cols.map((c) => c.header),
    ...rows.map((row) => cols.map((c) => toCell(c.accessor(row)))),
  ];

  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, "Data");
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });

  const blob = new Blob([out], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename.endsWith(".xlsx") ? filename : `${filename}.xlsx`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}